import { React, useState, useCallback, useEffect, useRef } from "react";
import AppWrapper from "../AppWrapper";
import {
  BlockStack,
  Button,
  Badge,
  Card,
  Checkbox,
  InlineGrid,
  Page,
  Text,
  TextField,
  DropZone,
  Banner,
  Select,
  Divider,
  Icon,
  Thumbnail,
  InlineStack,
  Spinner,
  Tag,
} from "@shopify/polaris";

import {
  SearchIcon,
  NoteIcon,
  PlayCircleIcon,
  FileIcon,
  DeleteIcon,
} from "@shopify/polaris-icons";
import supabase from "../../supabase-client";
import { formatPlate } from "../../utility/funcs";
import { useNavigate, useSearchParams } from "react-router-dom";
import { parseCfdiInvoice } from "../../utility/cfdiParser";
import {
  attachReplacementInvoice,
  serviceHoldingInvoice,
} from "../../utility/serviceInvoices";

// Why we are paying before the CFDI exists. A closed list on purpose: the
// structured answer is the audit trail, and free text is only asked for when
// none of them fit.
const QUOTE_REASONS = [
  { label: "Seleccionar...", value: "" },
  { label: "El taller pide anticipo para refacciones", value: "refacciones" },
  { label: "El taller factura al terminar el trabajo", value: "factura_al_final" },
  { label: "Grúa / traslado urgente", value: "urgente" },
  { label: "Otro", value: "otro" },
];

// A provider sitting on an invoice it never sent does not get another anticipo.
const OVERDUE_DAYS = 30;

function NewService() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preloadedPlate = searchParams.get("plate");

  const [plateToSearch, setPlateToSearch] = useState(searchParams.get("plate"));
  const [subleaseMatches, setSubleaseMatches] = useState(null); // multiple-match chooser
  const [car, setCar] = useState(undefined);
  const [carSearchError, setCarSearchError] = useState(undefined);
  const [carConfirmed, setCarConfirmed] = useState(false);
  const [newOdometer, setNewOdometer] = useState(undefined);

  // ?fieldService=<id>: attach the CFDI to a service the provider already
  // documented from their phone (FieldCapture). Car, odometer and provider come
  // from that service; saving updates it via complete_field_service instead of
  // inserting a new one.
  const fieldServiceId = searchParams.get("fieldService");
  const [fieldService, setFieldService] = useState(null);
  const [fieldError, setFieldError] = useState(null);

  const [xmlFile, setXmlFile] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [duplicateInvoice, setDuplicateInvoice] = useState(null);
  // Open anticipos from the same provider, surfaced when their CFDI shows up.
  const [pendingQuoteMatches, setPendingQuoteMatches] = useState(null);
  // The CFDI replaces one a service already holds (the provider cancelled
  // it): { cfdi, provider, formal, rows: [{ service_id, plate, folio, total,
  // replaces }] }. Attached to that service, never registered as a new one.
  const [replacementFor, setReplacementFor] = useState(null);
  const [replacementSaving, setReplacementSaving] = useState(null);
  const [replacementError, setReplacementError] = useState(null);

  const [cfdiInfo, setCfdiInfo] = useState(undefined);
  const [concepts, setConcepts] = useState(undefined);
  const [existingProvider, setExistingProvider] = useState(undefined);

  const [conceptTypes, setConceptTypes] = useState([]);
  const [conceptsSelected, setConceptsSelected] = useState(false);
  // AI-suggested types per concepto, keyed by index. The Select stays the
  // source of truth — this only records what was suggested and how sure, so
  // the UI can mark a guess the human should look at.
  const [typeSuggestions, setTypeSuggestions] = useState({});
  const [suggestingTypes, setSuggestingTypes] = useState(false);
  const [conceptsFinished, setConceptsFinished] = useState(false);

  // Grouped service-type rows (one per service_type). Built when leaving the
  // line-items step. Positions live here, not on individual CFDI conceptos,
  // because shops invoice inconsistently (3 tires as qty=1 or as 3 lines).
  const [groupedItems, setGroupedItems] = useState([]);
  const [positionsFinished, setPositionsFinished] = useState(false);

  const [files, setFiles] = useState([]);
  const [notes, setNotes] = useState("");

  // "Ya fue pagado": the service was paid before it reached the system (card at
  // the counter, cash, an earlier transfer). The payment is recorded up front;
  // the service still goes through approval, which then ratifies it rather than
  // authorising a payment that already happened.
  const [alreadyPaid, setAlreadyPaid] = useState(false);
  const [paidMethod, setPaidMethod] = useState("tarjeta");
  const [paidProof, setPaidProof] = useState(null);

  // ---- Quote-first path ("Continuar sin factura") -------------------------
  // Some providers demand payment before they stamp the CFDI. The service is
  // created from the cotización instead, runs through the same approval and
  // payment, and then waits for its invoice. `quoted_total` is frozen at
  // creation and the CFDI is later checked against it to the centavo.
  const [quoteMode, setQuoteMode] = useState(false);
  const [providerQuery, setProviderQuery] = useState("");
  const [providerResults, setProviderResults] = useState([]);
  const [providerSearching, setProviderSearching] = useState(false);
  const [quoteProvider, setQuoteProvider] = useState(null);
  const [providerOverdue, setProviderOverdue] = useState(null);
  const [quoteReason, setQuoteReason] = useState("");
  const [quoteReasonNote, setQuoteReasonNote] = useState("");
  const [quotationFile, setQuotationFile] = useState(null);
  const [expectedDate, setExpectedDate] = useState(
    new Date().toISOString().slice(0, 10),
  );
  const [quoteRows, setQuoteRows] = useState([
    { type: "", descripcion: "", total: "" },
  ]);
  // "Leer cotización": rows filled from the document by parse_service_quote.
  // The rows stay editable; warnings say what a person should double-check.
  const [readingQuote, setReadingQuote] = useState(false);
  const [quoteReadError, setQuoteReadError] = useState(null);
  const [quoteReadWarnings, setQuoteReadWarnings] = useState(null);
  // The file being read; a result for any other file (replaced mid-read) is
  // dropped instead of overwriting the rows.
  const readingFileRef = useRef(null);

  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);

  // Positions exposed in the picker, per service_types.position_kind.
  const POSITION_OPTIONS = {
    axle: [
      { value: "front_axle", label: "Eje delantero" },
      { value: "rear_axle", label: "Eje trasero" },
    ],
    corner: [
      { value: "FL", label: "Del. Izq." },
      { value: "FR", label: "Del. Der." },
      { value: "RL", label: "Tras. Izq." },
      { value: "RR", label: "Tras. Der." },
    ],
    wheel: [
      { value: "FL", label: "Del. Izq." },
      { value: "FR", label: "Del. Der." },
      { value: "RL_outer", label: "Tras. Izq. Ext." },
      { value: "RL_inner", label: "Tras. Izq. Int." },
      { value: "RR_outer", label: "Tras. Der. Ext." },
      { value: "RR_inner", label: "Tras. Der. Int." },
    ],
  };

  // Build the grouped-by-service_type rows from the per-concepto array.
  const buildGroupedItems = (rows = concepts) => {
    const map = rows.reduce((acc, c) => {
      const type = Number(c.type);
      if (!acc[type]) {
        const meta = conceptTypes.find((t) => t.id === type);
        acc[type] = {
          type,
          type_name: meta?.name || "",
          position_kind: meta?.position_kind || "none",
          subtotal_sin_iva: 0,
          iva_amount: 0,
          total: 0,
          descriptions: [],
          positions: [],
          includes: [],
        };
      }
      (c.included || []).forEach((t) => {
        if (!acc[type].includes.includes(Number(t)))
          acc[type].includes.push(Number(t));
      });
      acc[type].subtotal_sin_iva += Number(c.importe);
      acc[type].iva_amount += Number(c.iva || 0);
      acc[type].total += Number(c.total);
      acc[type].descriptions.push(c.descripcion);
      return acc;
    }, {});
    return Object.values(map);
  };

  const togglePosition = (groupIdx, pos) => {
    setGroupedItems((prev) =>
      prev.map((g, i) => {
        if (i !== groupIdx) return g;
        const has = g.positions.includes(pos);
        return {
          ...g,
          positions: has
            ? g.positions.filter((p) => p !== pos)
            : [...g.positions, pos],
        };
      }),
    );
  };

  // A cotización is quoted IVA included — that is the figure the provider will
  // stamp. The subtotal is derived so quoted rows carry the same shape as CFDI
  // conceptos, and the service total is the plain sum of the rows: one number,
  // no drift between what was typed, approved, paid and later invoiced.
  const quoteRowAmounts = (row) => {
    const total = Math.round((Number(row.total) || 0) * 100) / 100;
    const importe = Math.round((total / 1.16) * 100) / 100;
    return { total, importe, iva: Math.round((total - importe) * 100) / 100 };
  };
  const quoteTotal =
    Math.round(
      quoteRows.reduce((acc, r) => acc + quoteRowAmounts(r).total, 0) * 100,
    ) / 100;
  const quoteReady =
    !readingQuote &&
    !!quoteProvider &&
    !providerOverdue &&
    !!quoteReason &&
    (quoteReason !== "otro" || quoteReasonNote.trim().length > 0) &&
    !!expectedDate &&
    quoteRows.length > 0 &&
    quoteRows.every(
      (r) => r.type && r.descripcion.trim() && Number(r.total) > 0,
    );

  const loadConceptTypes = async () => {
    if (conceptTypes.length > 0) return;
    const { data } = await supabase
      .from("service_types")
      .select("*")
      .order("services_made", { ascending: false });
    setConceptTypes(data || []);
  };

  const searchProviders = async (value) => {
    setProviderQuery(value);
    setQuoteProvider(null);
    setProviderOverdue(null);
    if (!value || value.trim().length < 2) {
      setProviderResults([]);
      return;
    }
    setProviderSearching(true);
    const { data } = await supabase.rpc("search_service_providers", {
      search_text: value.trim(),
    });
    setProviderResults(data || []);
    setProviderSearching(false);
  };

  // The block that gives the whole thing teeth: a provider owing us a CFDI for
  // more than OVERDUE_DAYS cannot be paid up front again until it arrives.
  const selectQuoteProvider = async (p) => {
    setQuoteProvider(p);
    setProviderQuery(p.name);
    setProviderResults([]);
    const cutoff = new Date(
      Date.now() - OVERDUE_DAYS * 24 * 60 * 60 * 1000,
    ).toISOString();
    const { data } = await supabase
      .from("services")
      .select("id, created_at, quoted_total")
      .eq("provider", p.id)
      .eq("invoice_status", "pending")
      .not("quoted_total", "is", null)
      .lt("created_at", cutoff)
      .order("created_at", { ascending: true });
    setProviderOverdue(data && data.length ? data : null);
  };

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result).split(",")[1] || "");
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });

  // Read the cotización into quote rows as soon as it is dropped: the edge
  // function transcribes and reconciles amounts against the printed total; the
  // same classifier as the CFDI path then picks each row's service type.
  const readQuotation = async (file) => {
    readingFileRef.current = file;
    const stale = () => readingFileRef.current !== file;
    setReadingQuote(true);
    setQuoteReadError(null);
    setQuoteReadWarnings(null);
    try {
      const { data, error } = await supabase.functions.invoke(
        "parse_service_quote",
        {
          body: {
            file: await fileToBase64(file),
            media_type: file.type,
          },
        },
      );
      if (error) {
        let detail = "";
        try {
          detail = (await error.context?.json())?.error || "";
        } catch {
          /* non-JSON body */
        }
        throw new Error(detail || error.message);
      }
      if (stale()) return;
      if (data?.error) throw new Error(data.error);
      if (!data?.items?.length)
        throw new Error("No se encontraron conceptos con precio en la cotización.");

      const warnings = [...(data.warnings || [])];
      const rows = data.items.map((it) => ({
        type: "",
        descripcion: it.descripcion,
        total: String(it.total),
      }));

      await loadConceptTypes();
      try {
        const { data: cat } = await supabase.functions.invoke(
          "check_service_categorization",
          {
            body: {
              items: rows.map((r, i) => ({
                key: String(i),
                description: r.descripcion,
                unit_price: Number(r.total) / 1.16,
              })),
            },
          },
        );
        (cat?.items || []).forEach((c) => {
          const row = rows[Number(c.key)];
          if (row && c.suggested_type_id != null)
            row.type = String(c.suggested_type_id);
        });
      } catch {
        /* types stay blank and are picked by hand */
      }
      if (stale()) return;
      setQuoteRows(rows);

      const rfc = data.provider?.rfc;
      if (fieldService) {
        if (rfc && fieldService.provider?.rfc && rfc !== fieldService.provider.rfc)
          warnings.unshift(
            `La cotización es de ${data.provider.name || rfc} (${rfc}), pero el servicio está asignado a ${fieldService.provider.name} (${fieldService.provider.rfc}).`,
          );
      } else if (rfc && !quoteProvider) {
        const { data: prov } = await supabase
          .from("service_providers")
          .select("*")
          .eq("rfc", rfc)
          .maybeSingle();
        if (prov) await selectQuoteProvider(prov);
        else
          warnings.unshift(
            `${data.provider.name || "El proveedor"} (${rfc}) no está dado de alta; búscalo o regístralo primero.`,
          );
      }
      setQuoteReadWarnings(warnings);
    } catch (err) {
      if (!stale())
        setQuoteReadError(err.message || "No se pudo leer la cotización.");
    } finally {
      if (!stale()) setReadingQuote(false);
    }
  };

  const updateQuoteRow = (index, field, value) => {
    setQuoteRows((prev) =>
      prev.map((r, i) => (i === index ? { ...r, [field]: value } : r)),
    );
  };

  const needsPositions = groupedItems.some((g) => g.position_kind !== "none");
  const allPositionsPicked = groupedItems.every(
    (g) => g.position_kind === "none" || g.positions.length > 0,
  );

  // Read the CFDI and decide whether it belongs to a service that already
  // exists. Three ways it can:
  //   - the same UUID was registered before (a re-upload),
  //   - it replaces a CFDI the provider cancelled (relación 04, or a service of
  //     theirs SAT reported cancelled) — it goes on that service, or
  //   - this provider was paid up front and still owes us a CFDI, in which case
  //     this is almost certainly that invoice. Registering it as a new service
  //     would pay the same work twice, and nothing downstream would notice.
  const parseAndContinue = async () => {
    const cfdi = parseCfdiInvoice(await xmlFile.text());

    // Check if this invoice UUID already exists
    const { data: existingService } = await supabase
      .from("services")
      .select("id, car(plate)")
      .eq("invoice", cfdi.uuid)
      .maybeSingle();
    if (existingService) {
      setDuplicateInvoice({
        serviceId: existingService.id,
        plate: existingService.car?.plate,
        uuid: cfdi.uuid,
      });
      return;
    }
    setDuplicateInvoice(null);

    // It says it substitutes (relación 04) the CFDI of a service we have: the
    // provider cancelled that one. It goes on that service, with its payment.
    const held = await serviceHoldingInvoice(cfdi.substitutes);
    if (held) {
      setReplacementFor({ cfdi, formal: true, rows: [held] });
      return;
    }

    const { data: provider, error: existingProviderError } = await supabase
      .from("service_providers")
      .select("id")
      .eq("rfc", cfdi.emisorRfc)
      .maybeSingle();
    if (existingProviderError || !provider) {
      console.error(
        "Provider lookup failed:",
        existingProviderError,
        "RFC searched:",
        cfdi.emisorRfc,
      );
    }

    if (fieldService) {
      // The CFDI must come from the provider the service was assigned to.
      if (!provider || provider.id !== fieldService.provider?.id) {
        setFieldError(
          `La factura es de ${cfdi.emisorNombre || cfdi.emisorRfc}, pero el servicio #${fieldService.id} está asignado a ${fieldService.provider?.name} (${fieldService.provider?.rfc}).`,
        );
        return;
      }
      setFieldError(null);
      await proceedWithCfdi(cfdi, provider.id);
      return;
    }

    if (provider) {
      // No formal link, but this provider has services whose CFDI SAT reported
      // cancelled: this is most likely the replacement of one of them.
      const { data: cancelled } = await supabase
        .from("services")
        .select("id, invoice_id, total, car(plate), sublease_plate")
        .eq("provider", provider.id)
        .eq("invoice_status", "cancelled")
        .order("created_at", { ascending: true });
      if (cancelled && cancelled.length) {
        setReplacementFor({
          cfdi,
          provider,
          formal: false,
          rows: cancelled.map((r) => ({
            service_id: r.id,
            plate: r.car?.plate || r.sublease_plate,
            folio: r.invoice_id,
            total: r.total,
            replaces: null,
          })),
        });
        return;
      }
    }

    await continueWithProvider(cfdi, provider);
  };

  const continueWithProvider = async (cfdi, provider) => {
    setReplacementFor(null);
    if (provider) {
      const { data: openQuotes } = await supabase
        .from("services")
        .select("id, quoted_total, created_at, car(plate), sublease_plate")
        .eq("provider", provider.id)
        .eq("invoice_status", "pending")
        .not("quoted_total", "is", null)
        .order("created_at", { ascending: true });
      if (openQuotes && openQuotes.length) {
        setPendingQuoteMatches({ cfdi, providerId: provider.id, rows: openQuotes });
        return;
      }
    }

    await proceedWithCfdi(cfdi, provider ? provider.id : false);
  };

  const attachAsReplacement = async (row) => {
    setReplacementError(null);
    setReplacementSaving(row.service_id);
    try {
      await attachReplacementInvoice({
        serviceId: row.service_id,
        cfdi: replacementFor.cfdi,
        xmlFile,
        pdfFile,
        replaces: row.replaces,
      });
      navigate(`/services/${row.service_id}`);
    } catch (err) {
      setReplacementError(err.message);
    } finally {
      setReplacementSaving(null);
    }
  };

  const proceedWithCfdi = async (cfdi, providerId) => {
    setPendingQuoteMatches(null);
    setCfdiInfo(cfdi);
    const parsedConcepts =
      cfdi.conceptos.map((c) => ({
        ...c,
        type: "",
        included: [],
      })) || [];
    setConcepts(parsedConcepts);
    // Pre-fill the type Selects. Suggestions only — every one stays
    // editable, and a failure here just leaves them blank as before.
    suggestTypes(parsedConcepts);
    setExistingProvider(providerId);
    await loadConceptTypes();
  };

  useEffect(() => {
    if (!fieldServiceId) return;
    (async () => {
      const { data: svc, error } = await supabase
        .from("services")
        .select(
          "id, status, origin, odometer, provider(id, name, rfc), car(*, model(id, name, image_url, brand(id, name)))",
        )
        .eq("id", fieldServiceId)
        .single();
      if (error || !svc || svc.origin !== "field") {
        setFieldError("No se encontró el servicio por liga.");
        return;
      }
      if (svc.status !== "awaiting_invoice") {
        setFieldError(
          `El servicio #${svc.id} no está esperando factura (el proveedor aún no envía la captura o ya se completó).`,
        );
        return;
      }
      const { data: link } = await supabase
        .from("service_capture_links")
        .select("provider_notes")
        .eq("service_id", svc.id)
        .is("revoked_at", null)
        .maybeSingle();
      setFieldService(svc);
      setCar(svc.car);
      setNewOdometer(svc.odometer);
      setNotes(link?.provider_notes || "");
      setCarConfirmed(true);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldServiceId]);

  const buildServiceItemRows = (serviceId) =>
    groupedItems.map((g) => {
      const units =
        g.position_kind === "none" ? 1 : g.positions.length || 1;
      const ivaRate =
        g.subtotal_sin_iva > 0
          ? Math.round((g.iva_amount / g.subtotal_sin_iva) * 100) / 100
          : 0.16;
      return {
        service: serviceId,
        type: g.type,
        // legacy columns (kept until ViewService / reports migrate)
        quantity: 1,
        unit_price: g.subtotal_sin_iva,
        total: g.total,
        description: g.descriptions.join(" + "),
        // structured columns added in 20260522_service_items_structured
        subtotal_sin_iva: g.subtotal_sin_iva,
        iva_amount: g.iva_amount,
        iva_rate: ivaRate,
        units_completed: units,
        positions: g.position_kind === "none" ? null : g.positions,
        // Other work billed inside this line. insert_service_items stores it
        // as $0 rows (service_items.included_in) and drops any type that has
        // its own priced line in this same invoice.
        includes: g.includes || [],
      };
    });

  const uploadInvoiceFiles = async () => {
    const invoicePath =
      cfdiInfo.fechaTimbrado.split("T")[0].replace(/-/g, "/") +
      "/" +
      cfdiInfo.uuid;
    const { error: pdfError } = await supabase.storage
      .from("incoming-invoices")
      .upload(invoicePath + ".pdf", pdfFile);
    const { error: xmlError } = await supabase.storage
      .from("incoming-invoices")
      .upload(invoicePath + ".xml", xmlFile);
    if (pdfError || xmlError) {
      console.error(pdfError, xmlError);
    }
  };

  const recordUpfrontPayment = async (serviceId, amount) => {
    const proofExt = paidProof ? paidProof.name.split(".").pop() : null;
    const { data: paymentRow, error: paymentError } = await supabase
      .from("service_payments")
      .insert({
        service: serviceId,
        amount,
        method: paidMethod,
        file_ext: proofExt,
        // Never email a "comprobante de pago" when we do not have one.
        notify: !!paidProof,
      })
      .select("id")
      .single();
    if (paymentError) {
      // The service is already saved; say so instead of leaving it silently
      // unpaid.
      console.error("Payment insert failed:", paymentError);
      alert(
        `El servicio #${serviceId} se guardó, pero el pago no se pudo registrar: ${paymentError.message}. Regístralo desde la página del servicio.`,
      );
    } else if (paidProof && paymentRow) {
      await supabase.storage
        .from("services-evidence")
        .upload(`payments/${paymentRow.id}.${proofExt}`, paidProof);
    }
  };

  // Paying a link service from the cotización: quote_field_service freezes
  // the total from these items and sends it to approval; the CFDI is attached
  // later from the service page, same as any anticipo.
  const quoteFieldService = async () => {
    setUploading(true);
    setLoading(true);
    const { error } = await supabase.rpc("quote_field_service", {
      p_service: fieldService.id,
      p_reason: quoteReason,
      p_reason_note: quoteReasonNote,
      p_notes: notes,
      p_odometer: newOdometer ? Number(newOdometer) : null,
      p_items: buildServiceItemRows(fieldService.id),
    });
    if (error) {
      console.error(error);
      setFieldError(error.message);
      setUploading(false);
      setLoading(false);
      return;
    }
    if (quotationFile) {
      const ext = quotationFile.name.split(".").pop();
      const { error: quoteFileError } = await supabase.storage
        .from("services-evidence")
        .upload(`${fieldService.id}/cotizacion.${ext}`, quotationFile);
      if (quoteFileError) console.error(quoteFileError);
    }
    if (alreadyPaid) await recordUpfrontPayment(fieldService.id, quoteTotal);
    for (const evidenceFile of files) {
      await supabase.storage
        .from("services-evidence")
        .upload(`${fieldService.id}/${evidenceFile.name}`, evidenceFile);
    }
    navigate(`/services/${fieldService.id}`);
  };

  const completeFieldService = async () => {
    setUploading(true);
    setLoading(true);
    const { error } = await supabase.rpc("complete_field_service", {
      p_service: fieldService.id,
      p_invoice: cfdiInfo.uuid,
      p_invoice_id: cfdiInfo.folio,
      p_total: cfdiInfo.total,
      p_issuer_rfc: cfdiInfo.emisorRfc,
      p_stamped_at: cfdiInfo.fechaTimbrado,
      p_notes: notes,
      p_odometer: newOdometer ? Number(newOdometer) : null,
      p_items: buildServiceItemRows(fieldService.id),
    });
    if (error) {
      console.error(error);
      setFieldError(error.message);
      setUploading(false);
      setLoading(false);
      return;
    }
    await uploadInvoiceFiles();
    if (alreadyPaid) await recordUpfrontPayment(fieldService.id, cfdiInfo.total);
    for (const evidenceFile of files) {
      await supabase.storage
        .from("services-evidence")
        .upload(`${fieldService.id}/${evidenceFile.name}`, evidenceFile);
    }
    navigate(`/services/${fieldService.id}`);
  };

  const handleServiceUpload = async () => {
    if (fieldService)
      return quoteMode ? quoteFieldService() : completeFieldService();
    setUploading(true);
    setLoading(true);

    //Create service record. A subleased vehicle has no cars row — we instead
    // link the reservation it lives on and snapshot its plate/VIN. car_brand /
    // car_model are populated from its model in both cases.
    const isSub = !!car.sublease;
    // The amount everything downstream keys off: the CFDI total, or — on the
    // quote path — the cotización total, which is also frozen into
    // quoted_total and is what the CFDI will have to match exactly.
    const serviceTotal = quoteMode ? quoteTotal : cfdiInfo.total;
    const serviceObject = {
      // A quote has no stamp date yet, so this is an estimate. It is corrected
      // to the real one by attach_service_invoice when the CFDI arrives.
      performed_at: quoteMode
        ? new Date(`${expectedDate}T12:00:00`).toISOString()
        : cfdiInfo.fechaTimbrado,
      car: isSub ? null : car.id,
      sublease_reservation_id: isSub ? car.reservationId : null,
      sublease_plate: isSub ? car.plate : null,
      sublease_vin: isSub ? car.vin : null,
      provider: quoteMode ? quoteProvider.id : existingProvider,
      odometer: newOdometer ? Number(newOdometer) : null,
      invoice: quoteMode ? null : cfdiInfo.uuid,
      notes,
      total: serviceTotal,
      car_brand: car.model.brand.id,
      car_model: car.model.id,
      invoice_id: quoteMode ? null : cfdiInfo.folio,
      origin: quoteMode ? "quote" : "invoice",
      invoice_status: quoteMode ? "pending" : "received",
      quoted_total: quoteMode ? serviceTotal : null,
      quote_reason: quoteMode ? quoteReason : null,
      quote_reason_note:
        quoteMode && quoteReason === "otro" ? quoteReasonNote.trim() : null,
    };

    const { data: serviceData, error: serviceError } = await supabase
      .from("services")
      .insert(serviceObject)
      .select("id");
    if (serviceError) {
      console.error(serviceError);
      return;
    }

    const serviceId = serviceData[0].id;

    const serviceItemsObject = buildServiceItemRows(serviceId);

    const { error: serviceItemsError } = await supabase.rpc(
      "insert_service_items",
      { p_service: serviceId, p_items: serviceItemsObject },
    );
    if (serviceItemsError) {
      console.error(serviceItemsError);
      await supabase.from("services").delete().eq("id", serviceId);
      return;
    }

    if (quoteMode) {
      // The cotización is the document the frozen total came from, so it lives
      // with the rest of the service evidence.
      if (quotationFile) {
        const ext = quotationFile.name.split(".").pop();
        const { error: quoteFileError } = await supabase.storage
          .from("services-evidence")
          .upload(`${serviceId}/cotizacion.${ext}`, quotationFile);
        if (quoteFileError) console.error(quoteFileError);
      }
    } else {
      await uploadInvoiceFiles();
    }

    // Record a payment that happened before the service reached the system.
    // The trigger updates total_paid; it deliberately does NOT move the service
    // past 'pending_approval', so approval still has to happen.
    if (alreadyPaid) await recordUpfrontPayment(serviceId, serviceTotal);

    //upload evidence files
    for (let index in files) {
      const evidenceFile = files[index];
      const evidenceFilePath = serviceId + "/" + evidenceFile.name;
      await supabase.storage
        .from("services-evidence")
        .upload(evidenceFilePath, evidenceFile);
    }
    //Finished
    //Navigate back to all services
    navigate("/services");
  };

  const primaryButtonComponent = () => {
    if (car === undefined || uploading) return <></>;
    if (conceptsFinished) {
      // Positions step sits between line items and evidence, but only when
      // at least one chosen service_type tracks positions.
      if (needsPositions && !positionsFinished) {
        return (
          <Button
            variant="primary"
            disabled={!allPositionsPicked}
            onClick={() => setPositionsFinished(true)}
          >
            Siguiente
          </Button>
        );
      }
      return (
        <Button variant="primary" onClick={() => handleServiceUpload()}>
          Guardar
        </Button>
      );
    }
    if (quoteMode)
      return (
        <Button
          variant="primary"
          disabled={!quoteReady}
          onClick={() => {
            const quoteConcepts = quoteRows.map((r) => {
              const a = quoteRowAmounts(r);
              return {
                descripcion: r.descripcion.trim(),
                cantidad: 1,
                valorUnitario: a.importe,
                importe: a.importe,
                iva: a.iva,
                total: a.total,
                type: r.type,
              };
            });
            setConcepts(quoteConcepts);
            setGroupedItems(buildGroupedItems(quoteConcepts));
            setConceptsFinished(true);
          }}
        >
          Siguiente
        </Button>
      );
    if (cfdiInfo)
      return (
        <Button
          variant="primary"
          disabled={!conceptsSelected || !existingProvider}
          onClick={() => {
            setGroupedItems(buildGroupedItems());
            setConceptsFinished(true);
          }}
        >
          Siguiente
        </Button>
      );
    if (carConfirmed && xmlFile && pdfFile)
      return (
        <Button variant="primary" onClick={parseAndContinue}>
          Siguiente
        </Button>
      );

    // No factura yet (or only half of it): the provider is asking to be paid
    // first. Offer the quote path instead of a button that does nothing. A
    // link service already has its provider, so it is picked for them.
    if (carConfirmed)
      return (
        <Button
          variant="primary"
          onClick={async () => {
            await loadConceptTypes();
            if (fieldService) await selectQuoteProvider(fieldService.provider);
            setQuoteMode(true);
          }}
        >
          Continuar sin factura
        </Button>
      );

    if (car)
      return (
        <Button variant="primary" onClick={() => setCarConfirmed(true)}>
          Siguiente
        </Button>
      );
  };

  const backButtonComponent = () => {
    if (loading) return { onAction: () => {} };
    if (car === undefined) {
      if (preloadedPlate)
        return {
          url: "/services_dashboard",
        };
      return {
        url: "/services",
      };
    }
    if (conceptsFinished) {
      // Inside evidence step (positions already finished or not needed) -> back to positions / line items
      if (!needsPositions || positionsFinished) {
        return {
          onAction: () => {
            if (needsPositions) setPositionsFinished(false);
            else setConceptsFinished(false);
          },
        };
      }
      // Inside positions step -> back to line items
      return {
        onAction: () => {
          setConceptsFinished(false);
        },
      };
    }
    if (quoteMode)
      return {
        onAction: () => setQuoteMode(false),
      };
    if (cfdiInfo)
      return {
        onAction: () => {
          setCfdiInfo(undefined);
          setConceptsSelected(false);
        },
      };
    if (carConfirmed && fieldService)
      return { url: `/services/${fieldService.id}` };
    if (carConfirmed)
      return {
        onAction: () => {
          setPdfFile(null);
          setXmlFile(null);
          setCarConfirmed(false);
        },
      };
    if (car)
      return {
        onAction: () => {
          setCar(undefined);
        },
      };
  };

  const handlePlateChange = (value, id) => {
    setPlateToSearch(value.toUpperCase());
  };

  // One search box for any vehicle: try the owned fleet first (exact plate or
  // VIN), then fall back to subleased vehicles, which live inline on a
  // reservation (is_sublease). The same subleased vehicle can appear across
  // several leases, so present a chooser when more than one matches.
  const handleVehicleSearch = async (e) => {
    e.preventDefault();
    setCarSearchError(undefined);
    setSubleaseMatches(null);
    const q = (plateToSearch || "").trim();
    if (!q) return;

    const { data: owned } = await supabase
      .from("cars")
      .select("*, model(id, name, image_url, brand(id, name))")
      .or(`plate.eq.${q},vin.eq.${q}`)
      .limit(1);
    if (owned && owned.length) {
      setNewOdometer(owned[0].odometer);
      setCar(owned[0]);
      return;
    }

    const { data: subs } = await supabase
      .from("reservations")
      .select(
        "id, sublease_plate, sublease_vin, sublease_model:car_models!sublease_model_id(id, name, image_url, brand:car_brands(id, name))",
      )
      .eq("is_sublease", true)
      .or(`sublease_plate.ilike.%${q}%,sublease_vin.ilike.%${q}%`)
      .order("id", { ascending: false })
      .limit(20);
    if (subs && subs.length === 1) selectSublease(subs[0]);
    else if (subs && subs.length > 1) setSubleaseMatches(subs);
    else setCarSearchError("No se encontró la placa o VIN (ni en flotilla ni en subarriendos).");
  };

  const selectSublease = (r) => {
    setSubleaseMatches(null);
    setCarSearchError(undefined);
    setNewOdometer("");
    setCar({
      sublease: true,
      reservationId: r.id,
      vin: r.sublease_vin,
      plate: r.sublease_plate,
      model: {
        id: r.sublease_model?.id,
        name: r.sublease_model?.name || "",
        image_url: r.sublease_model?.image_url || "",
        brand: { id: r.sublease_model?.brand?.id, name: r.sublease_model?.brand?.name || "" },
      },
    });
  };

  const carSearchComponent = () => {
    return (
      <Card>
        <BlockStack gap={300}>
          <Text variant="headingMd">Vehiculo</Text>
          <form onSubmit={handleVehicleSearch}>
            <div
              style={{
                display: "flex",
                gap: "0.25rem",
              }}
            >
              <span style={{ flexGrow: 1 }}>
                <TextField
                  autoComplete="off"
                  placeholder="Búsqueda por placa o VIN"
                  value={plateToSearch}
                  onChange={handlePlateChange}
                  error={carSearchError}
                />
              </span>
              <span
                style={{
                  aspectRatio: 1,
                  display: "flex",
                  alignItems: "center",
                  width: 32,
                  height: 32,
                }}
              >
                <Button
                  icon={SearchIcon}
                  submit
                  disabled={!plateToSearch}
                />
              </span>
            </div>
          </form>
          {subleaseMatches && subleaseMatches.length > 1 && (
            <BlockStack gap={200}>
              <Text tone="subdued" variant="bodySm">
                Varios subarriendos coinciden — elige uno:
              </Text>
              {subleaseMatches.map((r) => (
                <Card key={r.id} subdued>
                  <InlineStack align="space-between" blockAlign="center">
                    <BlockStack>
                      <Text variant="bodyMd">
                        <strong>{r.sublease_plate}</strong>
                        {r.sublease_vin ? ` · ${r.sublease_vin}` : ""}
                      </Text>
                      <Text tone="subdued" variant="bodySm">
                        {r.sublease_model?.brand?.name} {r.sublease_model?.name} · Reserva #{r.id}
                      </Text>
                    </BlockStack>
                    <Button onClick={() => selectSublease(r)}>Elegir</Button>
                  </InlineStack>
                </Card>
              ))}
            </BlockStack>
          )}
        </BlockStack>
      </Card>
    );
  };

  const carDetailComponent = () => {
    return (
      <Card>
        <InlineGrid columns={2} alignItems="center" gap={300}>
          <div
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              justifyContent: "right",
            }}
          >
            <img
              alt={car.model.name}
              src={car.model.image_url}
              style={{ width: "200px", height: "200px", alignSelf: "center" }}
            />
          </div>
          <BlockStack align="center">
            {car.sublease && (
              <InlineStack>
                <Badge tone="magic">Subarrendado</Badge>
              </InlineStack>
            )}
            <Text variant="headingMd" tone="subdued">
              {car.vin}
            </Text>
            <Text variant="headingLg">{formatPlate(car.plate)}</Text>
            <Text variant="headingMd" tone="subdued">
              {car.model.brand.name} | {car.model.name}
            </Text>
          </BlockStack>
        </InlineGrid>
      </Card>
    );
  };

  const handleFileDrop = (_dropFiles, acceptedFiles) => {
    for (const file of acceptedFiles) {
      const name = file.name.toLowerCase();
      if (name.endsWith(".xml")) setXmlFile(file);
      if (name.endsWith(".pdf")) setPdfFile(file);
    }
    setDuplicateInvoice(null);
    setPendingQuoteMatches(null);
    setReplacementFor(null);
    setReplacementError(null);
  };

  const invoiceUploadComponent = () => {
    return (
      <Card>
        <BlockStack gap={300}>
          <Text variant="headingMd" as="h2">
            Subir Factura
          </Text>

          <DropZone
            accept={"application/pdf, text/xml, application/xml"}
            allowMultiple
            onDrop={handleFileDrop}
          >
            <DropZone.FileUpload
              actionTitle="Seleccionar Archivos"
              actionHint="Sube el PDF y XML de tu factura"
            />
          </DropZone>

          <BlockStack>
            {pdfFile && (
              <Banner tone="success">
                <p>PDF cargado: {pdfFile.name}</p>
              </Banner>
            )}
            {xmlFile && (
              <Banner tone="success">
                <p>XML cargado: {xmlFile.name}</p>
              </Banner>
            )}
            {!xmlFile && !pdfFile && (
              <Banner tone="info">
                <p>Sube ambos archivos: XML y PDF.</p>
              </Banner>
            )}
            {/* The CFDI of a service we already paid up front. Attaching it
                there is almost always the right move; creating a second
                service would double-pay the same work. */}
            {pendingQuoteMatches && (
              <Banner
                tone="critical"
                title="Este proveedor tiene servicios esperando factura"
              >
                <BlockStack gap={200}>
                  <p>
                    ¿Esta factura es de uno de estos servicios? Adjúntala ahí en
                    lugar de crear uno nuevo.
                  </p>
                  {pendingQuoteMatches.rows.map((r) => (
                    <InlineStack
                      key={r.id}
                      align="space-between"
                      blockAlign="center"
                    >
                      <Text variant="bodySm">
                        #{r.id} · {r.car?.plate || r.sublease_plate || "—"} · $
                        {Number(r.quoted_total).toLocaleString("es-MX", {
                          minimumFractionDigits: 2,
                        })}{" "}
                        · {String(r.created_at).substring(0, 10)}
                      </Text>
                      <Button onClick={() => navigate(`/services/${r.id}`)}>
                        Adjuntar aquí
                      </Button>
                    </InlineStack>
                  ))}
                  <div>
                    <Button
                      variant="plain"
                      onClick={() =>
                        proceedWithCfdi(
                          pendingQuoteMatches.cfdi,
                          pendingQuoteMatches.providerId,
                        )
                      }
                    >
                      Es un servicio distinto, continuar
                    </Button>
                  </div>
                </BlockStack>
              </Banner>
            )}
            {replacementFor && (
              <Banner
                tone="critical"
                title={
                  replacementFor.formal
                    ? `Esta factura sustituye a la del servicio #${replacementFor.rows[0].service_id}`
                    : "Este proveedor tiene facturas canceladas ante el SAT"
                }
              >
                <BlockStack gap={200}>
                  <p>
                    {replacementFor.formal
                      ? "El proveedor canceló la factura anterior y emitió esta en su lugar. Se adjunta a ese servicio y su pago pasa a esta factura; la anterior queda en el historial."
                      : "¿Esta factura es la sustituta de una de ellas? Adjúntala a ese servicio en lugar de crear uno nuevo: el pago ya hecho pasa a esta factura."}
                  </p>
                  {replacementFor.rows.map((r) => (
                    <InlineStack
                      key={r.service_id}
                      align="space-between"
                      blockAlign="center"
                    >
                      <Text variant="bodySm">
                        #{r.service_id} · {r.plate || "—"}
                        {r.folio ? ` · Factura ${r.folio}` : ""}
                        {r.total != null
                          ? ` · $${Number(r.total).toLocaleString("es-MX", { minimumFractionDigits: 2 })}`
                          : ""}
                      </Text>
                      <Button
                        variant="primary"
                        loading={replacementSaving === r.service_id}
                        disabled={replacementSaving !== null}
                        onClick={() => attachAsReplacement(r)}
                      >
                        {`Adjuntar al servicio #${r.service_id}`}
                      </Button>
                    </InlineStack>
                  ))}
                  {replacementError && (
                    <Text tone="critical">{replacementError}</Text>
                  )}
                  {!replacementFor.formal && (
                    <div>
                      <Button
                        variant="plain"
                        onClick={() =>
                          continueWithProvider(
                            replacementFor.cfdi,
                            replacementFor.provider,
                          )
                        }
                      >
                        Es un servicio distinto, continuar
                      </Button>
                    </div>
                  )}
                </BlockStack>
              </Banner>
            )}
            {duplicateInvoice && (
              <Banner
                tone="critical"
                title="Factura duplicada"
                action={{
                  content: "Ver servicio existente",
                  onAction: () =>
                    navigate(`/services/${duplicateInvoice.serviceId}`),
                }}
              >
                <p>
                  Esta factura (UUID {duplicateInvoice.uuid}) ya fue registrada
                  en otro servicio
                  {duplicateInvoice.plate
                    ? ` para la placa ${duplicateInvoice.plate}`
                    : ""}
                  .
                </p>
              </Banner>
            )}
          </BlockStack>
        </BlockStack>
      </Card>
    );
  };

  const productOptions = [
    { label: "Seleccionar...", value: "" },
    ...conceptTypes.map((s) => ({
      label: `${s.name} - ${s.description}`,
      value: s.id.toString(), // or s.name if you prefer text
    })),
  ];

  const suggestTypes = async (parsedConcepts) => {
    if (!parsedConcepts?.length) return;
    setSuggestingTypes(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "check_service_categorization",
        {
          body: {
            items: parsedConcepts.map((c, i) => ({
              key: String(i),
              description: c.descripcion,
              unit_price: c.valorUnitario,
            })),
          },
        }
      );
      if (error || data?.error) return; // silent: manual selection still works
      const byKey = {};
      (data.items || []).forEach((r) => {
        if (r.suggested_type_id != null) byKey[r.key] = r;
      });
      if (Object.keys(byKey).length === 0) return;
      setTypeSuggestions(byKey);
      setConcepts((prev) => {
        const updated = prev.map((c, i) =>
          byKey[String(i)]
            ? {
                ...c,
                type: String(byKey[String(i)].suggested_type_id),
                included: (byKey[String(i)].included_type_ids || []).map(
                  String
                ),
              }
            : c
        );
        setConceptsSelected(updated.every((c) => c.type && c.type !== ""));
        return updated;
      });
    } catch {
      /* suggestions are optional */
    } finally {
      setSuggestingTypes(false);
    }
  };

  const handleTypeChange = (index, newType) => {
    const updated = [...concepts];
    updated[index].type = newType;
    const allHaveType = updated.every((c) => c.type && c.type !== "");
    setConcepts(updated);
    setConceptsSelected(allHaveType);
    // Once a human picks, the suggestion is no longer what is on screen.
    setTypeSuggestions((prev) => {
      if (!prev[String(index)]) return prev;
      const next = { ...prev };
      delete next[String(index)];
      return next;
    });
  };

  // Work billed inside a concepto besides its main type (service 1250: one
  // "servicio" line that also changed the air filter). Recording it lets the
  // visit be compared against visits that did the same work; the price stays
  // on the concepto, nothing is split.
  const setIncluded = (index, included) => {
    setConcepts((prev) =>
      prev.map((c, i) => (i === index ? { ...c, included } : c))
    );
  };

  const typeName = (id) =>
    conceptTypes.find((t) => String(t.id) === String(id))?.name || id;

  const lineItemsConfirmationComponent = () => {
    return (
      <Card>
        <BlockStack gap={300}>
          <InlineStack gap={200} blockAlign="center">
            <Text variant="headingMd">Confirmar Conceptos</Text>
            {suggestingTypes && <Spinner size="small" />}
          </InlineStack>
          {Object.keys(typeSuggestions).length > 0 && (
            <Banner tone="info">
              <p>
                Los tipos se rellenaron automáticamente a partir de la
                descripción de cada concepto. Revísalos y corrige los que no
                correspondan antes de continuar.
              </p>
            </Banner>
          )}

          {/* --- Invoice Header --- */}
          <BlockStack gap={200}>
            {existingProvider !== false || (
              <Banner tone="warning">
                Este proveedor aún no está registrado.
              </Banner>
            )}
            <Text variant="bodyMd">
              <strong>Emisor:</strong> {cfdiInfo.emisorNombre} (
              {cfdiInfo.emisorRfc})
            </Text>
            <Text variant="bodyMd">
              <strong>Fecha timbrado:</strong> {cfdiInfo.fechaTimbrado}
            </Text>
            <div>
              <Text variant="bodyMd">
                <strong>Subtotal:</strong> ${cfdiInfo.subTotal.toFixed(2)}
              </Text>
              <Text variant="bodyMd">
                <strong>IVA:</strong> ${cfdiInfo.iva.toFixed(2)}
              </Text>
              <Text variant="bodyMd">
                <strong>Total:</strong> ${cfdiInfo.total.toFixed(2)}
              </Text>
            </div>
          </BlockStack>
          <Divider />
          {/* --- Concepts --- */}
          <BlockStack gap={200}>
            {concepts.map((item, i) => (
              <Card key={i} subdued>
                <BlockStack alignment="center" distribution="fillEvenly">
                  <Text variant="bodyMd">
                    <strong>{item.descripcion}</strong>
                  </Text>
                  <Text variant="bodySm" tone="subdued">
                    Cantidad: {item.cantidad} × ${item.valorUnitario.toFixed(2)}
                    <br />
                    IVA: ${item.iva.toFixed(2)}
                    <br />
                    Total: ${item.total.toFixed(2)}
                  </Text>

                  <Select
                    label="Tipo de gasto"
                    labelHidden
                    options={productOptions}
                    value={item.type}
                    onChange={(value) => handleTypeChange(i, value)}
                  />
                  {typeSuggestions[String(i)] && (
                    <Text
                      variant="bodySm"
                      tone={
                        typeSuggestions[String(i)].confidence === "high"
                          ? "subdued"
                          : "caution"
                      }
                    >
                      {typeSuggestions[String(i)].confidence === "high"
                        ? "Sugerido por IA"
                        : `Sugerido por IA (confianza ${
                            typeSuggestions[String(i)].confidence
                          }) — verifica`}
                    </Text>
                  )}
                  {item.type && (
                    <InlineStack gap={100} blockAlign="center" wrap>
                      <Text variant="bodySm" tone="subdued">
                        Incluye también:
                      </Text>
                      {(item.included || []).map((t) => (
                        <Tag
                          key={t}
                          onRemove={() =>
                            setIncluded(
                              i,
                              item.included.filter((x) => x !== t)
                            )
                          }
                        >
                          {typeName(t)}
                        </Tag>
                      ))}
                      <div style={{ minWidth: "180px" }}>
                        <Select
                          label="Agregar trabajo incluido"
                          labelHidden
                          options={[
                            { label: "+ Agregar trabajo", value: "" },
                            ...conceptTypes
                              .filter(
                                (t) =>
                                  String(t.id) !== item.type &&
                                  !(item.included || []).includes(String(t.id))
                              )
                              .map((t) => ({
                                label: t.name,
                                value: String(t.id),
                              })),
                          ]}
                          value=""
                          onChange={(v) =>
                            v &&
                            setIncluded(i, [...(item.included || []), v])
                          }
                        />
                      </div>
                    </InlineStack>
                  )}
                </BlockStack>
              </Card>
            ))}
          </BlockStack>
        </BlockStack>
      </Card>
    );
  };

  const handleDropZoneDrop = useCallback(
    (_dropFiles, acceptedFiles, _rejectedFiles) => {
      setFiles((prev) => [...prev, ...acceptedFiles]);
    },
    [],
  );

  const fileUpload = !files.length && (
    <DropZone.FileUpload
      actionHint="Arrastra tus archivos o haz clic para subirlos"
      actionTitle="Seleccionar Archivos"
    />
  );

  const fileList = files.map((file, index) => (
    <InlineStack
      key={index}
      align="space-between"
      blockAlign="center"
      gap={200}
    >
      <InlineStack align="start" gap={200}>
        <Thumbnail
          size="small"
          source={
            file.type.startsWith("image/")
              ? window.URL.createObjectURL(file)
              : file.type.startsWith("video/")
                ? PlayCircleIcon
                : FileIcon
          }
          alt={file.name}
        />
        <Text variant="bodySm">{file.name}</Text>
      </InlineStack>
      <div style={{ marginRight: "0.25rem" }}>
        <Button
          icon={DeleteIcon}
          tone="critical"
          variant="tertiary"
          onClick={(e) => {
            e.stopPropagation();
            setFiles((prev) => prev.filter((_, i) => i !== index));
          }}
        />
      </div>
    </InlineStack>
  ));

  const evidenceComponent = () => {
    return (
      <Card>
        <BlockStack gap={300}>
          <Text variant="headingMd">Adjunta Evidencia</Text>
          <TextField
            label="Odometro"
            value={newOdometer}
            onChange={setNewOdometer}
            type="number"
            requiredIndicator
          />
          <DropZone
            accept="image/*,video/*,application/pdf"
            type="file"
            allowMultiple
            onDrop={handleDropZoneDrop}
          >
            {fileUpload}
            {files.length > 0 && <BlockStack gap={200}>{fileList}</BlockStack>}
          </DropZone>

          <TextField
            label="Notas"
            multiline={3}
            value={notes}
            onChange={setNotes}
            prefix={<Icon source={NoteIcon} tone="subdued" />}
            placeholder="Escribe tus observaciones sobre el servicio..."
          />

          <Divider />

          <Checkbox
            label="Este servicio ya fue pagado"
            helpText="Márcalo si el pago se hizo antes de registrar el servicio (tarjeta, efectivo, transferencia previa)."
            checked={alreadyPaid}
            onChange={setAlreadyPaid}
          />

          {alreadyPaid && (
            <BlockStack gap={300}>
              <Select
                label="Forma de pago"
                options={[
                  { label: "Tarjeta", value: "tarjeta" },
                  { label: "Efectivo", value: "efectivo" },
                  { label: "Transferencia", value: "transferencia" },
                  { label: "SPEI", value: "spei" },
                  { label: "Otro", value: "otro" },
                ]}
                value={paidMethod}
                onChange={setPaidMethod}
              />

              <DropZone
                accept="image/*,application/pdf"
                type="file"
                allowMultiple={false}
                onDrop={(_d, accepted) => setPaidProof(accepted[0] || null)}
              >
                <DropZone.FileUpload
                  actionTitle="Adjuntar comprobante"
                  actionHint="Opcional. Si no hay comprobante, explica por qué abajo."
                />
              </DropZone>

              {paidProof ? (
                <Banner tone="success">
                  <p>Comprobante: {paidProof.name}</p>
                </Banner>
              ) : (
                <Banner tone="info" title="Sin comprobante">
                  <p>
                    Se registrará sin comprobante y quedará marcado así en el
                    servicio y en los reportes. La forma de pago queda anotada.
                  </p>
                </Banner>
              )}
            </BlockStack>
          )}
        </BlockStack>
      </Card>
    );
  };

  const quoteDetailsComponent = () => {
    return (
      <BlockStack gap={300}>
        <Banner tone="warning" title="Servicio sin factura">
          <p>
            Se registrará un servicio por cobrar sin CFDI. Pasa por la misma
            aprobación y el mismo pago, y queda marcado como <b>Falta factura</b>{" "}
            hasta que el proveedor la envíe. La factura tendrá que coincidir
            exactamente con el total cotizado.
          </p>
        </Banner>

        <Card>
          <BlockStack gap={300}>
            <Text variant="headingMd">Proveedor</Text>
            {/* A link service was assigned its provider when it was created. */}
            {!fieldService && (
              <TextField
                autoComplete="off"
                label="Buscar proveedor"
                labelHidden
                placeholder="Nombre o RFC del proveedor"
                value={providerQuery}
                onChange={searchProviders}
                suffix={providerSearching ? <Spinner size="small" /> : undefined}
              />
            )}
            {!fieldService && providerResults.length > 0 && (
              <BlockStack gap={200}>
                {providerResults.slice(0, 8).map((p) => (
                  <Card key={p.id} subdued>
                    <InlineStack align="space-between" blockAlign="center">
                      <BlockStack>
                        <Text variant="bodyMd">
                          <strong>{p.name}</strong>
                        </Text>
                        <Text tone="subdued" variant="bodySm">
                          {p.rfc}
                        </Text>
                      </BlockStack>
                      <Button onClick={() => selectQuoteProvider(p)}>
                        Elegir
                      </Button>
                    </InlineStack>
                  </Card>
                ))}
              </BlockStack>
            )}
            {quoteProvider && !providerOverdue && (
              <Banner tone="success">
                <p>
                  {quoteProvider.name} ({quoteProvider.rfc})
                </p>
              </Banner>
            )}
            {/* Nothing else makes a taller send the CFDI. */}
            {providerOverdue && (
              <Banner
                tone="critical"
                title="Este proveedor tiene facturas pendientes"
              >
                <p>
                  No se puede pagar por adelantado otra vez hasta que envíe las
                  facturas de:{" "}
                  {providerOverdue
                    .map((o) => `#${o.id}`)
                    .join(", ")}
                  .
                </p>
              </Banner>
            )}
          </BlockStack>
        </Card>

        <Card>
          <BlockStack gap={300}>
            <Text variant="headingMd">Motivo</Text>
            <Select
              label="¿Por qué se paga antes de la factura?"
              options={QUOTE_REASONS}
              value={quoteReason}
              onChange={setQuoteReason}
            />
            {quoteReason === "otro" && (
              <TextField
                label="Explica el motivo"
                multiline={2}
                value={quoteReasonNote}
                onChange={setQuoteReasonNote}
                requiredIndicator
              />
            )}
            {/* A link service already has its date: when the provider sent
                the photos. */}
            {!fieldService && (
              <TextField
                label="Fecha estimada del servicio"
                type="date"
                value={expectedDate}
                onChange={setExpectedDate}
                helpText="Se corrige automáticamente con la fecha del CFDI cuando llegue."
              />
            )}
            <DropZone
              accept="image/*,application/pdf"
              type="file"
              allowMultiple={false}
              onDrop={(_d, accepted) => {
                const file = accepted[0] || null;
                setQuotationFile(file);
                setQuoteReadError(null);
                setQuoteReadWarnings(null);
                if (file) readQuotation(file);
              }}
            >
              <DropZone.FileUpload
                actionTitle="Adjuntar cotización"
                actionHint="PDF o imagen de la cotización del proveedor"
              />
            </DropZone>
            {quotationFile ? (
              <BlockStack gap={200}>
                <Banner tone="success">
                  <p>Cotización: {quotationFile.name}</p>
                </Banner>
                {readingQuote && (
                  <InlineStack gap={200} blockAlign="center">
                    <Spinner size="small" />
                    <Text tone="subdued" variant="bodySm">
                      Leyendo conceptos y montos de la cotización…
                    </Text>
                  </InlineStack>
                )}
                {quoteReadError && (
                  <Banner tone="critical" title="No se pudo leer la cotización">
                    <p>{quoteReadError}</p>
                  </Banner>
                )}
                {quoteReadWarnings?.length > 0 && (
                  <Banner tone="warning" title="Revisa antes de continuar">
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                      {quoteReadWarnings.map((w, i) => (
                        <li key={i}>{w}</li>
                      ))}
                    </ul>
                  </Banner>
                )}
                {quoteReadWarnings?.length === 0 && (
                  <Text tone="subdued" variant="bodySm">
                    Conceptos cargados de la cotización. Revisa los tipos y
                    montos abajo.
                  </Text>
                )}
              </BlockStack>
            ) : (
              <Banner tone="info" title="Sin cotización">
                <p>
                  Se registrará sin cotización adjunta. El monto cotizado queda
                  congelado igual y la factura tendrá que coincidir con él.
                </p>
              </Banner>
            )}
          </BlockStack>
        </Card>

        <Card>
          <BlockStack gap={300}>
            <Text variant="headingMd">Qué se está pagando</Text>
            <Text tone="subdued" variant="bodySm">
              Captura los montos <strong>con IVA</strong>, tal como vienen en la
              cotización. La suma es el total que se autoriza, se paga y que la
              factura deberá decir.
            </Text>
            {quoteRows.map((row, i) => {
              const a = quoteRowAmounts(row);
              return (
                <Card key={i} subdued>
                  <BlockStack gap={200}>
                    <InlineStack align="space-between" blockAlign="center">
                      <Text variant="bodySm" tone="subdued">
                        Concepto {i + 1}
                      </Text>
                      {quoteRows.length > 1 && (
                        <Button
                          icon={DeleteIcon}
                          tone="critical"
                          variant="tertiary"
                          onClick={() =>
                            setQuoteRows((prev) =>
                              prev.filter((_, idx) => idx !== i),
                            )
                          }
                        />
                      )}
                    </InlineStack>
                    <Select
                      label="Tipo de gasto"
                      options={productOptions}
                      value={row.type}
                      onChange={(v) => updateQuoteRow(i, "type", v)}
                    />
                    <TextField
                      label="Descripción"
                      value={row.descripcion}
                      onChange={(v) => updateQuoteRow(i, "descripcion", v)}
                      placeholder="Ej. Cambio de balatas delanteras"
                    />
                    <TextField
                      label="Total con IVA"
                      type="number"
                      prefix="$"
                      value={row.total}
                      onChange={(v) => updateQuoteRow(i, "total", v)}
                      min={0}
                      helpText={
                        a.total > 0
                          ? `Subtotal $${a.importe.toFixed(
                              2,
                            )} + IVA $${a.iva.toFixed(2)}`
                          : undefined
                      }
                    />
                  </BlockStack>
                </Card>
              );
            })}
            <InlineStack align="space-between" blockAlign="center">
              <Button
                onClick={() =>
                  setQuoteRows((prev) => [
                    ...prev,
                    { type: "", descripcion: "", total: "" },
                  ])
                }
              >
                Agregar concepto
              </Button>
              <Text variant="headingMd">
                Total cotizado: $
                {quoteTotal.toLocaleString("es-MX", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </Text>
            </InlineStack>
          </BlockStack>
        </Card>
      </BlockStack>
    );
  };

  const uploadingComponent = () => {
    return (
      <div
        style={{
          display: "flex",
          alignContent: "center",
          justifyContent: "center",
        }}
      >
        <Spinner size="small" />
      </div>
    );
  };

  const positionsStepComponent = () => {
    return (
      <Card>
        <BlockStack gap={400}>
          <Text variant="headingMd">Posiciones intervenidas</Text>
          <Text tone="subdued" variant="bodySm">
            Marca las posiciones donde se realizó el trabajo, sin importar
            cómo lo facturó el proveedor. Esto permite comparar el costo por
            posición contra precios de mercado.
          </Text>
          {groupedItems.map((g, gi) => {
            if (g.position_kind === "none") return null;
            const opts = POSITION_OPTIONS[g.position_kind] || [];
            const perPos =
              g.positions.length > 0
                ? g.subtotal_sin_iva / g.positions.length
                : null;
            return (
              <Card key={gi} subdued>
                <BlockStack gap={200}>
                  <Text variant="bodyMd">
                    <strong>{g.type_name}</strong>
                  </Text>
                  <Text variant="bodySm" tone="subdued">
                    Subtotal: ${g.subtotal_sin_iva.toFixed(2)} · Total: $
                    {g.total.toFixed(2)}
                  </Text>
                  <InlineGrid
                    columns={g.position_kind === "wheel" ? 3 : 2}
                    gap={200}
                  >
                    {opts.map((opt) => {
                      const checked = g.positions.includes(opt.value);
                      return (
                        <Button
                          key={opt.value}
                          pressed={checked}
                          onClick={() => togglePosition(gi, opt.value)}
                        >
                          {opt.label}
                        </Button>
                      );
                    })}
                  </InlineGrid>
                  {perPos !== null ? (
                    <Text variant="bodySm" tone="subdued">
                      {g.positions.length} posición(es) · $
                      {perPos.toFixed(2)} por posición (sin IVA)
                    </Text>
                  ) : (
                    <Text variant="bodySm" tone="critical">
                      Selecciona al menos una posición
                    </Text>
                  )}
                </BlockStack>
              </Card>
            );
          })}
        </BlockStack>
      </Card>
    );
  };

  const pageContent = () => {
    if (car === undefined) return carSearchComponent();
    if (uploading) return uploadingComponent();
    if (conceptsFinished && needsPositions && !positionsFinished)
      return positionsStepComponent();
    if (conceptsFinished) return evidenceComponent();
    if (quoteMode) return quoteDetailsComponent();
    if (cfdiInfo) return lineItemsConfirmationComponent();
    if (carConfirmed) return invoiceUploadComponent();
    if (car) return carDetailComponent();
  };

  return (
    <AppWrapper>
      <Page
        title={
          fieldServiceId ? `Completar servicio #${fieldServiceId}` : "Nuevo Servicio"
        }
        subtitle={
          fieldService
            ? `Servicio por liga · ${fieldService.provider?.name} · sube la factura o la cotización del proveedor`
            : undefined
        }
        primaryAction={primaryButtonComponent()}
        backAction={backButtonComponent()}
      >
        <BlockStack gap={300}>
          {fieldError && <Banner tone="critical">{fieldError}</Banner>}
          {fieldServiceId && !fieldService
            ? !fieldError && <Spinner size="small" />
            : pageContent()}
        </BlockStack>
      </Page>
    </AppWrapper>
  );
}

export default NewService;
